import * as Collapsible from '@radix-ui/react-collapsible';
import { Archive, ChevronRight, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { Section } from '@/components/Section';
import { DataTable } from './DataTable';
import { JsonTree } from '@/components/JsonTree';
import { CopyJson } from '@/components/JsonCell';
import { Explain, Hint } from '@/components/Explain';
import { CopyId, KeyValue, Money } from '@/components/primitives';
import { cn } from '@/lib/cn';
import {
  formatDecimal,
  formatTime,
  parseEpochMs,
  titleize,
  type TimeZoneMode,
} from '@/lib/format';
import type { BetBundle } from '@/types';

type Props = { bundle: BetBundle; tz: TimeZoneMode };

const rows = (data: unknown): Record<string, unknown>[] =>
  (data as Record<string, unknown>[]) ?? [];

/* -------------------------------------------------------------------------- */

export function TransactionsSection({ bundle, tz }: Props) {
  return (
    <Section
      id="section-source_transactions"
      title="Transactions"
      term="source_transactions"
      source={bundle.transactions}
      count={bundle.transactions.data.length}
    >
      <DataTable
        rows={rows(bundle.transactions.data)}
        meta={bundle.currencyMetadata.data}
        tz={tz}
        timeKind="mysql"
      />
    </Section>
  );
}

export function GameBetSection({ bundle }: Props) {
  const bet = bundle.bet.data;
  return (
    <Section
      id="section-source_bet"
      title="Game bet row"
      term="source_bet"
      source={bundle.bet}
      count={bet ? 1 : 0}
      note="columns differ per game"
    >
      {bet && (
        // Per-game `bets` schemas genuinely diverge (~40 game DBs), so this is a
        // key/value grid rather than a fixed table.
        <div className="grid grid-cols-1 gap-x-8 px-4 py-2 md:grid-cols-2 lg:grid-cols-3">
          {Object.entries(bet).map(([k, v]) => (
            <KeyValue key={k} label={titleize(k)} term={k}>
              {renderLoose(v, bundle, k)}
            </KeyValue>
          ))}
        </div>
      )}
    </Section>
  );
}

function renderLoose(v: unknown, bundle: BetBundle, key: string) {
  if (v === null || v === undefined || v === '') return <span className="text-text-faint">—</span>;

  if (/amount$/.test(key)) {
    return <Money amount={v as string} meta={bundle.currencyMetadata.data} />;
  }
  // Odds/coefficients are decimal(38,18) too, but they are not money — they
  // never hit currency_metadata, so trim them here or they render as
  // "1.780000000000000000".
  if (/(odd|coefficient|multiplier|rtp)$/i.test(key)) {
    return <span>{formatDecimal(v)}</span>;
  }
  const epoch = /_time$/.test(key) ? parseEpochMs(v) : null;
  if (epoch) {
    return <span title={String(v)}>{formatTime(epoch, 'utc', true)} UTC</span>;
  }
  if (typeof v === 'object') return <JsonTree value={v} defaultOpen={0} />;

  const s = String(v);
  if (/(_id$|^id$)/.test(key)) return <CopyId value={s} truncate={s.length > 24} />;
  // Long values (avatar URLs, seeds) must not shove the next column aside.
  return (
    <span className="block truncate" title={s}>
      {s}
    </span>
  );
}

/* -------------------------------------------------------------------------- */

export function RoundDetailsSection({ bundle }: Props) {
  const rd = bundle.roundDetails.data;
  const result = rd?.details?.round_result as
    | { bet_on?: { message?: string; meta?: unknown }; round_result?: { message?: string; meta?: unknown } }
    | undefined;

  return (
    <Section
      id="section-source_roundDetails"
      title="Round details"
      term="source_roundDetails"
      source={bundle.roundDetails}
      count={rd ? 1 : 0}
    >
      {rd && (
        <div className="space-y-3 px-4 py-3">
          {/* Rendered from the raw payload we already hold — we never depend on
              the external round-reporter page loading. */}
          <div className="grid gap-2 md:grid-cols-2">
            <Outcome label="Player bet on" message={result?.bet_on?.message} meta={result?.bet_on?.meta} />
            <Outcome
              label="Round result"
              message={result?.round_result?.message}
              meta={result?.round_result?.meta}
              emphasis
            />
          </div>

          {rd.url && (
            <a
              href={rd.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1.5 border border-hairline px-2 py-1 font-condensed text-[10px] font-semibold uppercase tracking-wider text-accent hover:border-accent/40"
            >
              <ExternalLink size={11} />
              Open full round replay
            </a>
          )}
        </div>
      )}
    </Section>
  );
}

function Outcome({
  label,
  message,
  meta,
  emphasis,
}: {
  label: string;
  message?: string;
  meta?: unknown;
  emphasis?: boolean;
}) {
  return (
    <div className={cn('border border-hairline bg-surface-sunken p-3', emphasis && 'border-accent/30')}>
      <div className="eyebrow mb-1">{label}</div>
      <div className={cn('font-mono text-text', emphasis ? 'text-[15px]' : 'text-[13px]')}>
        {message ?? '—'}
      </div>
      {meta ? (
        <div className="mt-2 border-t border-hairline pt-1.5">
          <JsonTree value={meta} defaultOpen={1} />
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export function DeliverySection({ bundle, tz }: Props) {
  return (
    <Section
      id="section-source_delayDelivery"
      title="Delivery retries"
      term="source_delayDelivery"
      source={bundle.delayDelivery}
      count={bundle.delayDelivery.data.length}
    >
      <DataTable rows={rows(bundle.delayDelivery.data)} tz={tz} timeKind="mysql" />
    </Section>
  );
}

export function SessionsSection({ bundle }: Props) {
  const sessions = bundle.sessions.data ?? [];
  return (
    <Section
      id="section-source_sessions"
      title="Sessions"
      term="source_sessions"
      source={bundle.sessions}
      count={sessions.length}
      note={sessions.length > 1 ? 'multiple sessions — normal for active-state games' : undefined}
    >
      <div className="space-y-3 px-4 py-3">
        {sessions.map((s) => (
          <div key={s.id} className="border border-hairline">
            <div className="grid grid-cols-1 gap-x-8 border-b border-hairline bg-surface-sunken px-3 py-2 md:grid-cols-3">
              <KeyValue label="Session ID" term="session_id">
                <CopyId value={s.session_id} />
              </KeyValue>
              <KeyValue label="Player">
                <CopyId value={s.player_id} />
              </KeyValue>
              <KeyValue label="Currency">{s.currency ?? '—'}</KeyValue>
              <KeyValue label="Locale">{s.locale ?? '—'}</KeyValue>
              <KeyValue label="Demo">{s.is_demo ? 'yes' : 'no'}</KeyValue>
              <KeyValue label="Game session">
                <CopyId value={s.game_session_id} truncate />
              </KeyValue>
            </div>

            <div className="px-3 py-2">
              <div className="mb-1 flex items-center justify-between">
                <div className="eyebrow">
                  <Explain term="token_data">decoded session token</Explain>
                </div>
                {s.token_data_decoded ? (
                  <CopyJson value={s.token_data_decoded} label="copy token" />
                ) : null}
              </div>
              {s.token_data_decoded ? (
                <JsonTree value={s.token_data_decoded} defaultOpen={1} />
              ) : (
                <p className="text-[11.5px] text-text-faint">
                  No token stored, or it could not be decoded.
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function FreebetsSection({ bundle }: Props) {
  const freebets = bundle.freebets.data ?? [];
  return (
    <Section
      id="section-source_freebets"
      title="Freebets"
      term="source_freebets"
      source={bundle.freebets}
      count={freebets.length}
    >
      <div className="space-y-3 px-4 py-3">
        {freebets.map((f) => (
          <div key={f.id} className="border border-hairline">
            <div className="grid grid-cols-1 gap-x-8 px-3 py-2 md:grid-cols-3">
              <KeyValue label="Freebet ID">
                <CopyId value={f.id} />
              </KeyValue>
              <KeyValue label="Client freebet ID">
                <CopyId value={f.client_freebet_id} truncate />
              </KeyValue>
              <KeyValue label="Status">{f.status}</KeyValue>
              <KeyValue label="Bet amount">
                <Money amount={f.bet_amount} meta={bundle.currencyMetadata.data} currency={f.currency} />
              </KeyValue>
              <KeyValue label="Won amount">
                <Money amount={f.won_amount} meta={bundle.currencyMetadata.data} currency={f.currency} />
              </KeyValue>
              <KeyValue label="Used">
                <span className="tnum">
                  {f.played_freebet_count} / {f.total_freebet_count}
                </span>
              </KeyValue>
            </div>

            {Boolean(f.config || f.eligible_game_uuids) && (
              <div className="grid gap-3 border-t border-hairline px-3 py-2 md:grid-cols-2">
                {f.config ? (
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="eyebrow">config</span>
                      <CopyJson value={f.config} />
                    </div>
                    <JsonTree value={f.config} defaultOpen={2} />
                  </div>
                ) : null}
                {f.eligible_game_uuids ? (
                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="eyebrow">eligible games</span>
                      <CopyJson value={f.eligible_game_uuids} />
                    </div>
                    <JsonTree value={f.eligible_game_uuids} defaultOpen={2} />
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */

export function ClickhouseSections({ bundle, tz }: Props) {
  return (
    <>
      <Section
        id="section-source_clickhouseRealtime"
        title="Analytics — realtime"
        term="source_clickhouseRealtime"
        source={bundle.clickhouseRealtime}
        count={bundle.clickhouseRealtime.data.length}
        defaultOpen={false}
      >
        <DataTable rows={rows(bundle.clickhouseRealtime.data)} meta={bundle.currencyMetadata.data} tz={tz} timeKind="clickhouse" />
      </Section>

      <Section
        id="section-source_clickhouseMv"
        title="Analytics — attempt log"
        term="source_clickhouseMv"
        source={bundle.clickhouseMv}
        count={bundle.clickhouseMv.data.length}
        defaultOpen={false}
        note="includes failed attempts"
      >
        <DataTable rows={rows(bundle.clickhouseMv.data)} meta={bundle.currencyMetadata.data} tz={tz} timeKind="clickhouse" />
      </Section>

      <Section
        id="section-source_clickhouseSync"
        title="Analytics — settled"
        term="source_clickhouseSync"
        source={bundle.clickhouseSync}
        count={bundle.clickhouseSync.data.length}
        defaultOpen={false}
        note="~1h behind"
      >
        <DataTable rows={rows(bundle.clickhouseSync.data)} meta={bundle.currencyMetadata.data} tz={tz} timeKind="clickhouse" />
      </Section>
    </>
  );
}

/* -------------------------------------------------------------------------- */

export function LogsSection({ bundle, tz }: Props) {
  const logs = bundle.logs.data ?? [];
  const services = [
    ...new Set(logs.map((l) => String(l.labels?.service_name ?? '')).filter(Boolean)),
  ];

  return (
    <Section
      id="section-source_logs"
      title="Logs"
      term="source_logs"
      source={bundle.logs}
      count={logs.length}
      defaultOpen={false}
      note={services.length > 1 ? `${services.length} services` : undefined}
    >
      <CorrelationNote bundle={bundle} services={services} />
      <div className="divide-y divide-hairline/50">
        {logs.map((l, i) => (
          <LogLine key={i} entry={l} tz={tz} />
        ))}
      </div>
    </Section>
  );
}

/**
 * Explains HOW WIDE the net was. Logs are gathered by expanding from the bet id
 * through every trace we find, so a reader must know whether they are looking at
 * one service's view or the whole request path — and whether a cap cut it short.
 */
function CorrelationNote({
  bundle,
  services,
}: {
  bundle: BetBundle;
  services: string[];
}) {
  const c = bundle.correlation;
  if (!c) return null;

  return (
    <div className="border-b border-hairline bg-surface-sunken px-4 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-text-dim">
        <span>
          Expanded from bet {bundle.resolved.bet_id} through{' '}
          <span className="font-mono text-info">{c.traceIds.length}</span> trace
          {c.traceIds.length === 1 ? '' : 's'} across{' '}
          <span className="font-mono text-info">{services.length}</span> service
          {services.length === 1 ? '' : 's'} — including any that never logged the bet id.
        </span>
        {c.traceIds.length > 0 && (
          <span className="flex flex-wrap items-center gap-1">
            {c.traceIds.slice(0, 4).map((id) => (
              <CopyId key={id} value={id} truncate className="text-[10px] text-text-faint" />
            ))}
            {c.traceIds.length > 4 && (
              <span className="text-[10px] text-text-faint">+{c.traceIds.length - 4}</span>
            )}
          </span>
        )}
      </div>
      {c.truncated && (
        <p className="mt-1 border-t border-hairline pt-1 text-[11px] text-warn">
          ⚠ {c.truncated} — there may be more related logs than shown.
        </p>
      )}
    </div>
  );
}

function LogLine({ entry, tz }: { entry: BetBundle['logs']['data'][number]; tz: TimeZoneMode }) {
  const line = entry.line as Record<string, unknown>;
  const level = String(line.level ?? '').toLowerCase();
  const at = new Date(Number(entry.tsNs) / 1e6);

  return (
    <details className="group px-4 py-1.5 hover:bg-surface-raised/40">
      <summary className="flex cursor-pointer list-none items-baseline gap-2">
        <span className="tnum shrink-0 font-mono text-[11px] text-text-faint">
          {tz === 'utc' ? at.toISOString().slice(11, 19) : at.toLocaleTimeString()}
        </span>
        <span
          className={cn(
            'shrink-0 font-condensed text-[10px] font-semibold uppercase tracking-wider',
            level === 'error' || level === 'fatal'
              ? 'text-crit'
              : level === 'warn'
                ? 'text-warn'
                : 'text-text-faint',
          )}
        >
          {level || 'log'}
        </span>
        <span className="shrink-0 font-mono text-[11px] text-info">
          {String(entry.labels?.service_name ?? '')}
        </span>
        <span className="truncate font-mono text-[11.5px] text-text">
          {String(line.message ?? JSON.stringify(line).slice(0, 120))}
        </span>
        <ProvenanceBadge via={entry.discoveredVia} />
      </summary>
      <div className="mt-1.5 border-l border-hairline pl-3">
        <JsonTree value={line} defaultOpen={1} />
      </div>
    </details>
  );
}

/**
 * Why is this line here?
 *
 * A line from a service that never logged the bet id is only present because a
 * trace led us to it. Showing the route lets support judge whether it really
 * belongs to their bet instead of taking our word for it.
 */
function ProvenanceBadge({ via }: { via?: BetBundle['logs']['data'][number]['discoveredVia'] }) {
  if (!via) return null;

  const label =
    via.via === 'bet_id'
      ? 'names this bet'
      : via.via === 'tempo-search'
        ? 'via trace search'
        : `via trace ${via.value.slice(0, 8)}…`;

  return (
    <Hint
      side="left"
      content={
        <div className="space-y-1">
          <div className="eyebrow text-accent">how this was found</div>
          <ol className="space-y-0.5">
            {via.chain.map((step, i) => (
              <li key={step} className="font-mono text-[11px]">
                <span className="text-text-faint">{i === 0 ? '' : '↳ '}</span>
                <span className={i === via.chain.length - 1 ? 'text-text' : 'text-text-dim'}>
                  {step.startsWith('trace:') ? `trace ${step.slice(6, 14)}…` : step}
                </span>
              </li>
            ))}
          </ol>
          <p className="border-t border-hairline pt-1 text-[11px] text-text-dim">
            {via.via === 'bet_id'
              ? 'This line names the bet directly.'
              : 'This service never logged the bet id — we reached it by following the trace above.'}
          </p>
        </div>
      }
    >
      <span
        className={cn(
          'ml-auto shrink-0 cursor-help border px-1 py-[1px] font-mono text-[9.5px]',
          via.via === 'bet_id'
            ? 'border-hairline text-text-faint'
            : 'border-info/30 text-info',
        )}
      >
        {label}
      </span>
    </Hint>
  );
}

export function TracesSection({ bundle }: Props) {
  const traces = bundle.traces.data ?? [];
  return (
    <Section
      id="section-source_traces"
      title="Traces"
      term="source_traces"
      source={bundle.traces}
      count={traces.length}
      defaultOpen={false}
    >
      <div className="divide-y divide-hairline">
        {traces.map((t) => (
          <TraceRow key={t.traceId} trace={t} />
        ))}
      </div>
    </Section>
  );
}

function TraceRow({ trace }: { trace: BetBundle['traces']['data'][number] }) {
  const spans = trace.spans ?? [];
  const hasDetail = spans.length > 0;
  const services = new Set(spans.map((s) => s.service)).size;
  // Controlled expansion — a static `open` on <details> fights React and snaps
  // back whenever anything in the row re-renders (e.g. CopyId's copied state).
  const [open, setOpen] = useState(hasDetail);

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <div className="flex items-baseline gap-3 px-4 py-2">
        {/* Only the chevron+meta toggle; CopyId/badge stay independently clickable. */}
        <Collapsible.Trigger asChild>
          <button
            type="button"
            className="flex flex-1 items-baseline gap-3 text-left hover:opacity-90"
          >
            <ChevronRight
              size={11}
              className={cn(
                'shrink-0 self-center text-text-faint transition-transform',
                open && 'rotate-90',
              )}
            />
            <span className="font-mono text-[11.5px] text-info">
              {trace.rootServiceName ?? '—'}
            </span>
            {trace.durationMs !== undefined && (
              <span className="tnum font-mono text-[11px] text-text-dim">
                {trace.durationMs}ms
              </span>
            )}
            {hasDetail && (
              <span className="font-mono text-[10px] text-text-faint">
                {spans.length} span{spans.length === 1 ? '' : 's'} · {services} service
                {services === 1 ? '' : 's'}
              </span>
            )}
          </button>
        </Collapsible.Trigger>
        <CopyId value={trace.traceId} truncate className="text-[11px]" />
        <ProvenanceBadge via={trace.discoveredVia} />
      </div>

      <Collapsible.Content>
        <div className="px-4 pb-3 pl-[38px]">
          {hasDetail ? (
            <SpanGroups spans={spans} />
          ) : (
            <TraceDetailNote status={trace.detailStatus} error={trace.detailError} />
          )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

/**
 * Why there are no spans to show. An archived trace EXISTS — its detail is just
 * in cold storage — which is a completely different thing from a trace we
 * couldn't reach, so the two never read the same.
 */
function TraceDetailNote({
  status,
  error,
}: {
  status?: string;
  error?: string;
}) {
  if (status === 'archived') {
    return (
      <div className="flex items-start gap-2 border-l-2 border-l-warn bg-warn-bg px-3 py-2">
        <Archive size={13} className="mt-[1px] shrink-0 text-warn" />
        <p className="text-[12px] leading-relaxed text-text">
          This trace exists, but its span detail has been moved to{' '}
          <strong>cold storage</strong> and can't be loaded live. The summary above is
          still accurate; retrieving the full spans needs the trace restored from cold storage.
        </p>
      </div>
    );
  }
  if (status === 'unavailable') {
    return (
      <p className="border-l-2 border-l-crit bg-crit-bg px-3 py-2 text-[12px] text-text">
        Could not load this trace's spans.{error ? <span className="font-mono text-[11px] text-crit"> {error}</span> : null}
      </p>
    );
  }
  return (
    <p className="px-1 py-1 text-[11.5px] text-text-faint">
      Only the trace summary was retrieved (spans not fetched).
    </p>
  );
}

type Span = NonNullable<BetBundle['traces']['data'][number]['spans']>[number];

/**
 * Spans grouped by service — a trace crosses several services, so a flat list
 * hides "which service did what". Each service is a subgroup; the time-scale is
 * shared across ALL groups (relative to the whole trace) so the bars line up and
 * "where did the time go" reads at a glance.
 */
function SpanGroups({ spans }: { spans: Span[] }) {
  const total = Math.max(...spans.map((s) => s.startOffsetMs + s.durationMs), 1);

  // Group by service, preserving first-appearance order (roughly call order).
  const order: string[] = [];
  const byService = new Map<string, Span[]>();
  for (const s of spans) {
    if (!byService.has(s.service)) {
      byService.set(s.service, []);
      order.push(s.service);
    }
    byService.get(s.service)!.push(s);
  }

  return (
    <div className="space-y-2">
      {order.map((service) => {
        const group = byService.get(service)!;
        const busyMs = group.reduce((sum, s) => sum + s.durationMs, 0);
        const anyError = group.some((s) => s.status === 'error');
        return (
          <div key={service} className="border-l border-hairline pl-2">
            <div className="mb-0.5 flex items-baseline gap-2">
              <span className={cn('font-mono text-[11px]', anyError ? 'text-crit' : 'text-info')}>
                {service}
              </span>
              <span className="font-mono text-[10px] text-text-faint">
                {group.length} span{group.length === 1 ? '' : 's'} · {Math.round(busyMs)}ms
              </span>
            </div>
            <div className="space-y-[3px] font-mono">
              {group.map((s, i) => (
                <SpanBar key={`${s.spanId}-${i}`} span={s} total={total} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * One span. Click to expand its FULL attributes and the resource attributes of
 * the instance that emitted it — inline, not a truncated hover tooltip that
 * capped at 20 and dropped `code`. This is where "which pod, what error, what
 * rpc method" actually lives.
 */
function SpanBar({ span, total }: { span: Span; total: number }) {
  const [open, setOpen] = useState(false);
  const left = (span.startOffsetMs / total) * 100;
  const width = Math.max((span.durationMs / total) * 100, 0.6);
  const tone =
    span.status === 'error'
      ? 'bg-crit'
      : span.code && span.code !== 'OK'
        ? 'bg-warn'
        : 'bg-info';

  const spanAttrs = Object.entries(span.attributes);
  const resourceAttrs = Object.entries(span.resource ?? {});

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="grid w-full grid-cols-[11px_1fr_54px] items-center gap-2 rounded px-1 py-[1px] text-left hover:bg-surface-raised/60"
      >
        <ChevronRight
          size={10}
          className={cn('text-text-faint transition-transform', open && 'rotate-90')}
        />
        <div className="relative h-[13px] rounded-sm bg-surface-sunken">
          <div
            className={cn('absolute top-0 h-full rounded-sm', tone)}
            style={{ left: `${left}%`, width: `${width}%` }}
          />
          <span className="absolute inset-0 flex items-center truncate px-1 text-[10px] text-text">
            {span.name}
          </span>
        </div>
        <span className="tnum text-right text-[10px] text-text-dim">
          {span.durationMs < 1 ? '<1' : Math.round(span.durationMs)}ms
        </span>
      </button>

      {open && (
        <div className="ml-[19px] mt-1 space-y-2 border-l border-hairline pl-2">
          <div className="flex flex-wrap items-baseline gap-2 font-mono text-[10.5px] text-text-dim">
            <span
              className={cn(
                'eyebrow',
                span.status === 'error'
                  ? 'text-crit'
                  : span.code && span.code !== 'OK'
                    ? 'text-warn'
                    : 'text-info',
              )}
            >
              {span.status}
              {span.code ? ` · ${span.code}` : ''}
            </span>
            <span>start +{Math.round(span.startOffsetMs)}ms</span>
            <span>dur {span.durationMs < 1 ? '<1' : Math.round(span.durationMs)}ms</span>
            {span.spanId && <span className="text-text-faint">span {span.spanId}</span>}
          </div>
          <AttrTable title="span attributes" attrs={spanAttrs} />
          <AttrTable title="resource" attrs={resourceAttrs} />
        </div>
      )}
    </div>
  );
}

/** A flat key/value table for OTLP attributes — no truncation. */
function AttrTable({ title, attrs }: { title: string; attrs: [string, string][] }) {
  if (attrs.length === 0) {
    return (
      <div>
        <div className="eyebrow mb-0.5">{title}</div>
        <div className="font-mono text-[10.5px] text-text-faint">none</div>
      </div>
    );
  }
  return (
    <div>
      <div className="eyebrow mb-0.5">
        {title} <span className="text-text-faint">· {attrs.length}</span>
      </div>
      <div className="max-h-[260px] overflow-auto">
        <table className="w-full border-collapse font-mono text-[10.5px]">
          <tbody>
            {attrs.map(([k, v]) => (
              <tr key={k} className="border-b border-hairline/40 last:border-0 align-top">
                <td className="whitespace-nowrap py-[1px] pr-3 text-text-faint">{k}</td>
                <td className="break-all py-[1px] text-text">
                  <CopyId value={v} mono={false} className="text-text" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export function RtpSection({ bundle }: Props) {
  const rtp = bundle.rtpVariation.data;
  return (
    <Section
      id="section-source_rtpVariation"
      title="RTP variation"
      term="source_rtpVariation"
      source={bundle.rtpVariation}
      count={rtp ? 1 : 0}
      defaultOpen={false}
    >
      {rtp && (
        <div className="grid grid-cols-1 gap-x-8 px-4 py-2 md:grid-cols-3">
          {Object.entries(rtp).map(([k, v]) => (
            <KeyValue key={k} label={titleize(k)}>
              {String(v ?? '—')}
            </KeyValue>
          ))}
        </div>
      )}
    </Section>
  );
}

export function CurrencySection({ bundle }: Props) {
  const c = bundle.currencyMetadata.data;
  return (
    <Section
      id="section-source_currencyMetadata"
      title="Currency"
      term="source_currencyMetadata"
      source={bundle.currencyMetadata}
      count={c ? 1 : 0}
      defaultOpen={false}
    >
      {c && (
        <div className="grid grid-cols-1 gap-x-8 px-4 py-2 md:grid-cols-3">
          {Object.entries(c).map(([k, v]) => (
            <KeyValue key={k} label={titleize(k)}>
              {String(v ?? '—')}
            </KeyValue>
          ))}
        </div>
      )}
    </Section>
  );
}

export function RawSection({ bundle }: { bundle: BetBundle }) {
  return (
    <Section id="section-raw" title="Raw bundle" defaultOpen={false} note="everything, unformatted">
      <div className="px-4 py-3">
        <div className="mb-2 flex items-center justify-between border-b border-hairline pb-2">
          <span className="text-[11.5px] text-text-dim">
            The complete response, exactly as the API returned it — for pasting into a ticket.
          </span>
          <CopyJson value={bundle} label="copy whole bundle" />
        </div>
        <JsonTree value={bundle} defaultOpen={1} />
      </div>
    </Section>
  );
}
